import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { asyncForEach } from '@shared/utils/array';
import type { EstimateProps } from '@api/models/estimate';
import {
	updateCargo,
	updateCargoPort,
	updateEstimate,
	updateFixture,
	updatePeriodScheduleItem,
	updatePortRotation,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import { useCargoProvider } from './CargoContext';
import { usePortRotationProvider } from './PortRotationContext';
import { useEstimate } from './EstimatorContext';
import { useCentralStore } from './CentralStoreContext';

export type EstimateChangesContextType = {
	hasPendingChanges: boolean;
	onClearPendingChanges: () => void;
	onSaveChanges: (args?: { estimateAttributes?: Partial<EstimateProps> }) => Promise<void>;
	immediateSaveChanges: (
		stateChanges: (...args: any[]) => Promise<void> | void,
		serverChanges?: (...args: any[]) => Promise<void> | void
	) => Promise<void>;
	saving: boolean;
	useBlocker: any;
	makeBlockable: any;
};

export const EstimateChangesContext = createContext<EstimateChangesContextType>(undefined!);
export const useEstimateChangesProvider = () => useContext(EstimateChangesContext);

const EstimateChangesProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const [shouldSave, setShouldSave] = useState(false);
	const [saving, setSaving] = useState(false);
	const saveQueue = useRef<Promise<void>>(Promise.resolve());
	const activeSaves = useRef(0);
	const {
		estimate,
		pendingChanges: pendingEstimateChanges,
		setPendingChanges: setPendingEstimateChanges,
	} = useEstimate();

	const {
		pendingCargoChanges,
		pendingCargoPortChanges,
		setPendingCargoChanges,
		setPendingCargoPortChanges,
	} = useCargoProvider();

	const {
		refreshEverything,
		pendingFixtureChanges,
		setPendingFixtureChanges,
		fixtureDetails,
		pendingPeriodScheduleChanges,
		setPendingPeriodScheduleChanges,
	} = useCentralStore();

	const {
		pendingChanges: pendingPortRotationChanges,
		setPendingChanges: setPendingPortRotationChanges,
		pendingPortRotationOrderChanges,
		setPendingPortRotationOrderChanges,
	} = usePortRotationProvider();

	const hasPendingChanges = useMemo(() => {
		if (
			Object.keys(pendingEstimateChanges).length > 0 ||
			Object.keys(pendingFixtureChanges).length > 0 ||
			pendingCargoChanges.length > 0 ||
			pendingCargoPortChanges.length > 0 ||
			pendingPortRotationChanges.length > 0 ||
			pendingPortRotationOrderChanges.length > 0 ||
			pendingPeriodScheduleChanges.length > 0
		) {
			return true;
		}

		return false;
	}, [
		pendingCargoChanges,
		pendingCargoPortChanges,
		pendingEstimateChanges,
		pendingPortRotationChanges,
		pendingPortRotationOrderChanges,
		pendingFixtureChanges,
		pendingPeriodScheduleChanges,
	]);

	const pendingRef = useRef({
		estimateId: estimate?.id,
		pendingEstimateChanges,
		pendingFixtureChanges,
		fixtureDetails,
		pendingCargoChanges,
		pendingCargoPortChanges,
		pendingPeriodScheduleChanges,
		pendingPortRotationChanges,
		pendingPortRotationOrderChanges,
	});

	pendingRef.current = {
		estimateId: estimate?.id,
		pendingEstimateChanges,
		pendingFixtureChanges,
		fixtureDetails,
		pendingCargoChanges,
		pendingCargoPortChanges,
		pendingPeriodScheduleChanges,
		pendingPortRotationChanges,
		pendingPortRotationOrderChanges,
	};

	const onClearPendingChanges = useCallback(() => {
		setPendingEstimateChanges({});
		setPendingFixtureChanges({});
		setPendingPortRotationChanges([]);
		setPendingCargoChanges([]);
		setPendingCargoPortChanges([]);
		setPendingPortRotationOrderChanges([]);
		setPendingPeriodScheduleChanges([]);
	}, [
		setPendingCargoChanges, setPendingCargoPortChanges, setPendingEstimateChanges,
		setPendingPortRotationChanges, setPendingPortRotationOrderChanges, setPendingFixtureChanges,
		setPendingPeriodScheduleChanges,
	]);

	const performSaveChanges = useCallback(
		async (args?: { estimateAttributes?: Partial<EstimateProps> }) => {
			const {
				estimateId,
				pendingEstimateChanges: estimateChanges,
				pendingFixtureChanges: fixtureChanges,
				fixtureDetails: fixture,
				pendingCargoChanges: cargoChanges,
				pendingCargoPortChanges: cargoPortChanges,
				pendingPeriodScheduleChanges: periodScheduleChanges,
				pendingPortRotationChanges: portRotationChanges,
				pendingPortRotationOrderChanges: portRotationOrderChanges,
			} = pendingRef.current;

			if (estimateId == null) {
				return;
			}

			try {
				if (
					Object.keys(estimateChanges).length > 0 ||
					(
						args?.estimateAttributes != null &&
						Object.keys(args?.estimateAttributes).length > 0
					)
				) {
					await updateEstimate(estimateId, {
						...estimateChanges,
						...args?.estimateAttributes,
					});
				}

				if (fixture != null && Object.keys(fixtureChanges).length > 0) {
					await updateFixture(fixture.id, fixtureChanges);
				}

				if (cargoChanges.length > 0) {
					await asyncForEach(cargoChanges, async (c) => {
						await updateCargo(c, false);
					});
				}

				if (cargoPortChanges.length > 0) {
					await asyncForEach(
						cargoPortChanges,
						async (c) => await updateCargoPort(c, false),
						true,
					);
				}

				if (fixture != null && periodScheduleChanges.length > 0) {
					await asyncForEach(periodScheduleChanges, async (c) => {
						await updatePeriodScheduleItem(c.id, c);
					});
				}

				if (portRotationOrderChanges.length > 0) {
					await updatePortRotation(portRotationOrderChanges, estimateId);
				}

				if (portRotationChanges.length > 0) {
					await updateEstimate(estimateId, {
						portRotationEntries: portRotationChanges,
					});
				}

				await refreshEverything();
				onClearPendingChanges();
			} catch (e) {
				showErrorNotification('Could not update estimate', e as Error);
			}
		}, [onClearPendingChanges, refreshEverything],
	);

	const onSaveChanges = useCallback(
		async (args?: { estimateAttributes?: Partial<EstimateProps> }) => {
			setShouldSave(false);
			activeSaves.current += 1;
			setSaving(true);

			const queued = saveQueue.current.then(() => performSaveChanges(args));
			saveQueue.current = queued.catch(() => { });

			try {
				await queued;
			} finally {
				activeSaves.current -= 1;

				if (activeSaves.current === 0) {
					setSaving(false);
				}
			}
		}, [performSaveChanges],
	);

	useEffect(() => {
		if (shouldSave && !saving) {
			onSaveChanges();
		}
	}, [shouldSave, onSaveChanges, saving]);

	/*
	For actions that require immediate updates
	React is not easy to work with when it comes to updating local state
	and THEN updating the server with said values, so take care - usually,
	update local state first and then perform async server operations after
	*/
	const immediateSaveChanges = useCallback(async (
		stateChanges: (...args: any[]) => Promise<any> | any,
		serverChanges?: (...args: any[]) => Promise<any> | any,
	) => {
		await stateChanges();
		await serverChanges?.();
		setShouldSave(true);
	}, []);

	const {
		useBlocker,
		makeBlockable,
	} = useNavigationBlock();

	return (
		<EstimateChangesContext.Provider
			value={{
				hasPendingChanges,
				onClearPendingChanges,
				onSaveChanges,
				immediateSaveChanges,
				saving,
				useBlocker,
				makeBlockable,
			}}
		>
			{children}
		</EstimateChangesContext.Provider>
	);
};

export default EstimateChangesProvider;
